import { debug } from '../../utils/debug';
import React from 'react';
import { m } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FiArrowLeft, FiMapPin, FiDollarSign } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useTheme } from '../../context/ThemeContext';
import { useCurrency } from '../../context/CurrencyContext';
import { useMotionConfig } from '../../hooks/useReducedMotion';

const DeliveryAreaSelection = ({ bookingData, onAreaSelect, onBack }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { formatCurrency } = useCurrency();
  const motion = useMotionConfig();
  const location = bookingData.location;

  // Get delivery areas from location data
  const deliveryAreas = location?._apiData?.delivery_areas || location?.delivery_areas || [];

  debug.log('Delivery areas:', deliveryAreas);

  return (
    <div className="max-w-4xl mx-auto">
      {onBack && (
        <button
          onClick={onBack}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          aria-label="Back to location selection"
        >
          <SafeIcon icon={FiArrowLeft} className="w-5 h-5" aria-hidden="true" />
          <span>{t('delivery.backToLocation')}</span>
        </button>
      )}

      <m.div
        {...motion.fadeInUp}
        className="text-center mb-8"
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('delivery.chooseArea')}</h1>
        <p className="text-gray-600">{t('delivery.enterAddressLater')}</p>
      </m.div>

      <div className="grid md:grid-cols-2 gap-6">
        {deliveryAreas.map((area, index) => {
          const areaPrice = parseFloat(area.price) || 0;
          const isFree = areaPrice === 0;

          return (
            <m.button
              key={area.id || index}
              {...motion.stagger(index)}
              {...motion.noHover}
              type="button"
              className="cursor-pointer text-left w-full focus:outline-none focus:ring-2 focus:ring-offset-2 rounded-2xl"
              style={{ '--tw-ring-color': theme.colors.secondary.base }}
              onClick={() => onAreaSelect(area)}
              aria-label={`${area.name}, ${t('delivery.deliveryFee')}: ${isFree ? t('delivery.free') : formatCurrency(areaPrice)}`}
            >
              <div
                className="bg-white rounded-2xl shadow-lg p-6 border-2 border-transparent transition-all duration-300"
                onMouseEnter={(e) => e.currentTarget.style.borderColor = theme.colors.secondary.base}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = 'transparent'}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center"
                      style={{ background: theme.colors.secondary.gradient }}
                    >
                      <SafeIcon icon={FiMapPin} className="w-6 h-6" style={{ color: theme.colors.secondary.contrastIcon }} aria-hidden="true" />
                    </div>
                    <div>
                      <span className="text-xl font-bold text-gray-900">{area.name}</span>
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 font-medium">{t('delivery.deliveryFee')}</span>
                    <div className="text-right">
                      {isFree ? (
                        <span className="text-2xl font-bold text-green-600 uppercase">{t('delivery.free')}</span>
                      ) : (
                        <span className="text-2xl font-bold text-gray-900">
                          {formatCurrency(areaPrice)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </m.button>
          );
        })}
      </div>

      {deliveryAreas.length === 0 && (
        <m.div
          {...motion.fadeIn}
          className="text-center py-12 text-gray-600"
        >
          <p>No delivery areas configured for this location.</p>
        </m.div>
      )}
    </div>
  );
};

export default DeliveryAreaSelection;
